import * as React from 'react';
import { Checkbox, Input, Spin, Typography } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { Country, Centre, School } from 'app/shared/model';
import { formatSchoolEntityId } from '../utils';
import './HolidayManager.css';

type SchoolSelectorProps = {
    countries: Country[];
    centres: Centre[];
    schools: School[];
    selectedSchoolIds: string[];
    onSelectionChange: (schoolIds: string[]) => void;
    loading?: boolean;
    disabled?: boolean;
};

type SchoolWithMeta = School & {
    schoolId: string;
    countryId: string;
    centreName?: string;
};


const SchoolSelector: React.FC<SchoolSelectorProps> = ({
    countries,
    centres,
    schools,
    selectedSchoolIds,
    onSelectionChange,
    loading = false,
    disabled = false,
}) => {
    const [searchTerm, setSearchTerm] = React.useState('');
    const normalizedSearch = searchTerm.trim().toLowerCase();


    const centresByCode = React.useMemo(() => {
        const map = new Map<string, Centre>();
        centres.forEach((centre) => {
            map.set(String(centre.code), centre);
        });
        return map;
    }, [centres]);

    const schoolsByCountry = React.useMemo(() => {
        const grouped = new Map<string, SchoolWithMeta[]>();
        schools.forEach((school) => {
            const schoolId = formatSchoolEntityId(school.id ?? school.code);
            const centre = centresByCode.get(String(school.centreCode));
            const countryId = centre?.countryCode ? String(centre.countryCode) : '';
            if (!countryId || !schoolId) {
                return;
            }
            const list = grouped.get(countryId);
            const entry: SchoolWithMeta = {
                ...school,
                schoolId,
                countryId,
                centreName: centre?.name,
            };
            if (list) {
                list.push(entry);
            } else {
                grouped.set(countryId, [entry]);
            }
        });
        grouped.forEach((list) => list.sort((a, b) => a.name.localeCompare(b.name)));
        return grouped;
    }, [schools, centresByCode]);

    const filteredCountries = React.useMemo(() => {
        if (!normalizedSearch) {
            return countries;
        }
        return countries.filter((country) => {
            if (country.name.toLowerCase().includes(normalizedSearch)) {
                return true;
            }
            const countrySchools = schoolsByCountry.get(country.id) ?? [];
            return countrySchools.some((school) => {
                const centreName = school.centreName?.toLowerCase() ?? '';
                return school.name.toLowerCase().includes(normalizedSearch) || centreName.includes(normalizedSearch);
            });
        });
    }, [countries, normalizedSearch, schoolsByCountry]);

    const handleCountryToggle = (countryId: string, checked: boolean) => {
        const countrySchools = schoolsByCountry.get(countryId) ?? [];
        if (!countrySchools.length) {
            return;
        }
        const countrySchoolIds = countrySchools.map((x) => x.schoolId);
        if (checked) {
            const merged = new Set(selectedSchoolIds);
            countrySchoolIds.forEach((id) => merged.add(id));
            onSelectionChange(Array.from(merged));
        } else {
            const filtered = selectedSchoolIds.filter((id) => !countrySchoolIds.includes(id));
            onSelectionChange(filtered);
        }
    };

    const handleSchoolToggle = (schoolId: string, event: CheckboxChangeEvent) => {
        const { checked } = event.target;
        if (checked) {
            if (!selectedSchoolIds.includes(schoolId)) {
                onSelectionChange([...selectedSchoolIds, schoolId]);
            }
        } else {
            onSelectionChange(selectedSchoolIds.filter((id) => id !== schoolId));
        }
    };

    const renderCountryBlock = (country: Country) => {
        const countrySchools = schoolsByCountry.get(country.id) ?? [];
        if (!countrySchools.length) {
            return null;
        }
        const schoolMatches = normalizedSearch
            ? countrySchools.filter((school) => {
                  const centreName = school.centreName?.toLowerCase() ?? '';
                  return (
                      school.name.toLowerCase().includes(normalizedSearch) ||
                      centreName.includes(normalizedSearch)
                  );
              })
            : countrySchools;
        if (!schoolMatches.length) {
            return null;
        }
        const countrySchoolIds = schoolMatches.map((school) => school.schoolId);
        const selectedCount = countrySchoolIds.filter((id) => selectedSchoolIds.includes(id)).length;
        const allSelected = selectedCount === countrySchoolIds.length && countrySchoolIds.length > 0;
        const partiallySelected = selectedCount > 0 && selectedCount < countrySchoolIds.length;

        return (
            <div key={country.id} className="holiday-selector__country">
                <div className="holiday-selector__country-header">
                    <Checkbox
                        indeterminate={partiallySelected}
                        checked={allSelected}
                        disabled={disabled || loading}
                        onChange={(event) => handleCountryToggle(country.id, event.target.checked)}
                    >
                        <Typography.Text strong>{country.name}</Typography.Text>
                    </Checkbox>
                </div>
                <div className="holiday-selector__schools">
                    {schoolMatches.map((school) => (
                        <Checkbox
                            key={school.schoolId}
                            checked={selectedSchoolIds.includes(school.schoolId)}
                            disabled={disabled || loading}
                            onChange={(event) => handleSchoolToggle(school.schoolId, event)}
                        >
                            <span>{school.name}</span>
                        
                        </Checkbox>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="holiday-selector">
            <div className="holiday-selector__search">
                <Input
                    allowClear
                    size="middle"
                    placeholder="Search countries..."
                    value={searchTerm}
                    disabled={loading}
                    onChange={(event) => setSearchTerm(event.target.value)}
                />
            </div>
            <div className="holiday-selector__list">
                {loading ? (
                    <div className="holiday-selector__loading">
                        <Spin />
                    </div>
                ) : (
                    filteredCountries.map(renderCountryBlock)
                )}
                {!loading && filteredCountries.length === 0 ? (
                    <Typography.Text type="secondary">No countries found.</Typography.Text>
                ) : null}
            </div>
        </div>
    );
};

export default SchoolSelector;